import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import { ENTITY_STATUS, ENTITY_TYPE } from "../../constants";
import {
  ENTITY_REQUEST_ERROR_LABELS,
  ENTITY_STATUS_LABELS,
  ENTITY_TYPE_LABELS,
  fieldErrorLabel,
  missingFieldsText,
} from "./entityLabels";
import PhoneNumberField from "../generic/PhoneNumberField";
import {
  DEFAULT_PHONE_COUNTRY,
  countryCodeForName,
  splitE164,
} from "../../phoneCountries";
import { normalizeNit, validateEntity, verificationDigit } from "./entityLogic";
import { createEntity, updateEntity } from "../../services/backend/EntityService";
import { getEntityConfig, idTypeOptions, sectorOptions } from "../../services/backend/EntityConfigService";

const EMPTY = {
  nit: "",
  legalName: "",
  tradeName: "",
  entityType: ENTITY_TYPE.PUBLIC,
  sector: "",
  country: "Colombia",
  department: "",
  city: "",
  address: "",
  phone: "",
  email: "",
  website: "",
  legalRepName: "",
  legalRepIdType: "",
  legalRepIdNumber: "",
  legalRepEmail: "",
  legalRepPhone: "",
  legalRepPhoneCountryCode: "",
  status: ENTITY_STATUS.ACTIVE,
  notes: "",
};

// Los campos que el servidor NO acepta en una edición: el NIT es la llave del
// documento y el DV se calcula de él.
const IMMUTABLE = ["nit", "verificationDigit"];

// Alta y edición de una entidad. Valida con las MISMAS reglas que la Cloud
// Function (entityLogic, espejo de _shared/entityLogic), así que el servidor
// sólo debería rechazar carreras — y si lo hace, sus códigos de error se pintan
// con el mismo mapa de etiquetas.
// `selfService`: el mismo formulario abierto por la EMPRESA desde Mi Entidad, y
// no por un gestor desde Entidades. Cambia qué campos se ofrecen, no cómo se
// guarda: el estado, la razón social, la naturaleza jurídica y las
// observaciones son de la institución, y el backend los descarta de todas
// formas (ENTITY_ACTOR_IMMUTABLE en _shared/entityScope.js). Mostrarlos
// editables sería ofrecer controles que no hacen nada.
export default function EntityForm({ open, entity, onClose, onSaved, selfService = false }) {
  const user = useSelector((state) => state.user.value);
  const isEdit = !!entity;
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [requestError, setRequestError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [idTypes, setIdTypes] = useState([]);

  useEffect(() => {
    if (!open) return;
    setForm(entity ? { ...EMPTY, ...entity } : EMPTY);
    setErrors({});
    setRequestError(null);
  }, [open, entity]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const cfg = await getEntityConfig();
      setSectors(sectorOptions(cfg));
      setIdTypes(idTypeOptions(cfg));
    })();
  }, [open]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    // El error del campo se limpia al escribir: mantenerlo mientras se corrige
    // es ruido.
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  // El DV se calcula mientras se escribe. Es la única cifra del formulario que
  // la máquina puede verificar sola, así que se muestra al lado del NIT: un
  // dígito distinto al del RUT delata un NIT mal tecleado antes de guardarlo.
  const dv = useMemo(() => {
    const digits = normalizeNit(form.nit);
    return digits ? verificationDigit(digits) : null;
  }, [form.nit]);

  const handleSubmit = async () => {
    const { valid, errors: found } = validateEntity(form);
    if (!valid) {
      setErrors(found);
      // El diálogo es largo y el campo marcado puede quedar fuera de la vista:
      // arriba se NOMBRA cuál es.
      setRequestError(`Revisa: ${missingFieldsText(found)}.`);
      return;
    }
    setSaving(true);
    setRequestError(null);

    let result;
    if (isEdit) {
      const patch = { ...form };
      for (const key of IMMUTABLE) delete patch[key];
      result = await updateEntity(entity.nit, patch, user);
    } else {
      result = await createEntity({ ...form, nit: normalizeNit(form.nit) }, user);
    }
    setSaving(false);

    if (!result.ok) {
      setErrors(result.errors || {});
      // Un error de campo ya se ve sobre el campo; arriba sólo va lo que no
      // pertenece a ninguno (NIT repetido, permisos, red).
      if (!Object.keys(result.errors || {}).length) {
        setRequestError(
          ENTITY_REQUEST_ERROR_LABELS[result.error] ||
            "No se pudo guardar la entidad. Intenta de nuevo."
        );
      }
      return;
    }
    onSaved(result.data);
    onClose();
  };

  const fieldError = (name) => fieldErrorLabel(errors[name]) || undefined;

  // El teléfono del representante se guarda compuesto (E.164): el campo se llena
  // partiéndolo de vuelta y el país arranca en el de la entidad. Un número
  // anterior al selector se devuelve tal cual y sólo se compone si se edita.
  const legalRepCountry =
    form.legalRepPhoneCountryCode || countryCodeForName(form.country) || DEFAULT_PHONE_COUNTRY;
  const legalRepNational = splitE164(form.legalRepPhone, legalRepCountry).national;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{isEdit ? `Editar ${entity.legalName}` : "Nueva entidad"}</DialogTitle>
      <DialogContent dividers>
        {requestError && <Alert severity="error" sx={{ mb: 2 }}>{requestError}</Alert>}

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Identificación
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="NIT"
              value={form.nit}
              onChange={(e) => setField("nit", e.target.value)}
              // El NIT es el id del documento: cambiarlo sería otra entidad.
              disabled={isEdit}
              InputProps={{
                endAdornment: dv !== null && (
                  <InputAdornment position="end">-{dv}</InputAdornment>
                ),
              }}
              helperText={
                fieldError("nit") ||
                (isEdit
                  ? "El NIT no se puede cambiar."
                  : "Sin el dígito de verificación: se calcula solo.")
              }
              error={!!errors.nit}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Razón social"
              value={form.legalName}
              onChange={(e) => setField("legalName", e.target.value)}
              disabled={selfService}
              helperText={
                fieldError("legalName")
                || (selfService ? "La cambia el equipo de Extensión." : " ")
              }
              error={!!errors.legalName}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nombre comercial"
              value={form.tradeName}
              onChange={(e) => setField("tradeName", e.target.value)}
              helperText=" "
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              label="Naturaleza jurídica"
              value={form.entityType}
              onChange={(e) => setField("entityType", e.target.value)}
              disabled={selfService}
              helperText={
                fieldError("entityType")
                || (selfService
                  ? "Define qué documentos se te exigen. La cambia Extensión."
                  : "Define qué documentos se le exigen.")
              }
              error={!!errors.entityType}
              fullWidth
            >
              {Object.values(ENTITY_TYPE).map((t) => (
                <MenuItem key={t} value={t}>{ENTITY_TYPE_LABELS[t]}</MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* El estado NO se le ofrece a la empresa: es lo que concede la
              revisión de Extensión, y una entidad que se lo cambia sola se
              aprueba sola. */}
          {!selfService && (
            <Grid item xs={12} sm={3}>
              <TextField
                select
                label="Estado"
                value={form.status}
                onChange={(e) => setField("status", e.target.value)}
                helperText={fieldError("status") || " "}
                error={!!errors.status}
                fullWidth
              >
                {Object.values(ENTITY_STATUS).map((s) => (
                  <MenuItem key={s} value={s}>{ENTITY_STATUS_LABELS[s]}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            {/* freeSolo: la lista de sectores la mantiene la configuración, pero
                un sector nuevo no puede bloquear el registro de una entidad. */}
            <Autocomplete
              freeSolo
              options={sectors}
              value={form.sector || ""}
              onChange={(_, value) => setField("sector", value || "")}
              onInputChange={(_, value) => setField("sector", value || "")}
              renderInput={(params) => (
                <TextField {...params} label="Sector" helperText=" " fullWidth />
              )}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Contacto
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField label="País" value={form.country}
              onChange={(e) => setField("country", e.target.value)} helperText=" " fullWidth />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Departamento" value={form.department}
              onChange={(e) => setField("department", e.target.value)} helperText=" " fullWidth />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Ciudad" value={form.city}
              onChange={(e) => setField("city", e.target.value)} helperText=" " fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Dirección" value={form.address}
              onChange={(e) => setField("address", e.target.value)} helperText=" " fullWidth />
          </Grid>
          {/* Principal: la entidad puede tener varios correos y teléfonos, pero
              se administran desde su ficha (la sección Contactos). Aquí se edita
              el principal, que es el que sale en el listado. */}
          <Grid item xs={12} sm={3}>
            <TextField label="Teléfono principal" value={form.phone}
              onChange={(e) => setField("phone", e.target.value)} helperText=" " fullWidth />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Correo principal" value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              helperText={fieldError("email") || " "} error={!!errors.email} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Sitio web" value={form.website}
              onChange={(e) => setField("website", e.target.value)} helperText=" " fullWidth />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Representante legal
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label="Nombre" value={form.legalRepName}
              onChange={(e) => setField("legalRepName", e.target.value)} helperText=" " fullWidth />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField select label="Tipo de documento" value={form.legalRepIdType || ""}
              onChange={(e) => setField("legalRepIdType", e.target.value)} helperText=" " fullWidth>
              <MenuItem value="">Sin especificar</MenuItem>
              {idTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Número de documento" value={form.legalRepIdNumber}
              onChange={(e) => setField("legalRepIdNumber", e.target.value)} helperText=" " fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Correo" value={form.legalRepEmail}
              onChange={(e) => setField("legalRepEmail", e.target.value)}
              helperText={fieldError("legalRepEmail") || " "} error={!!errors.legalRepEmail} fullWidth />
          </Grid>
          <Grid item xs={12}>
            {/* Una fila entera: son dos controles (país + número) y en media
                columna el número se queda sin espacio. */}
            <Box sx={{ maxWidth: 520 }}>
              <PhoneNumberField
                label="Teléfono"
                countryCode={legalRepCountry}
                national={legalRepNational}
                onChange={({ countryCode, e164 }) => {
                  setField("legalRepPhone", e164);
                  setField("legalRepPhoneCountryCode", countryCode);
                }}
                error={!!errors.legalRepPhone}
                helperText={fieldError("legalRepPhone") || " "}
              />
            </Box>
          </Grid>
          {/* Observaciones es la libreta del gestor SOBRE la empresa, guardada
              en la ficha de la empresa. No es suya para leerla ni escribirla. */}
          {!selfService && (
            <Grid item xs={12}>
              <TextField label="Observaciones" value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                multiline minRows={2} helperText=" " fullWidth />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
